import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2, ShieldCheck, Star, Users } from "lucide-react";
import Bubbles from "@/components/site/Bubbles";
import { IMG } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHECKOUT_URL = "https://wa.me/5547999686547?text=Ol%C3%A1%2C%20quero%20aproveitar%20a%20promo%C3%A7%C3%A3o%202%20ingressos%20pelo%20pre%C3%A7o%20de%20um!";
const EASE = [0.16, 1, 0.3, 1];

// 8 questions engineered to build desire, remove objections and micro-commit
const QUESTIONS = [
  {
    id: "sonho",
    label: "Pergunta 1 de 8",
    title: "Você já imaginou como é respirar debaixo d'água pela primeira vez?",
    sub: "A maioria das pessoas nunca esquece esse momento.",
    options: [
      { key: "sim-sempre", label: "Sim, é um sonho antigo" },
      { key: "curioso", label: "Já pensei, mas nunca tive coragem" },
      { key: "atrai", label: "Sempre me chamou atenção" },
    ],
  },
  {
    id: "atracao",
    label: "Pergunta 2 de 8",
    title: "O que mais te encanta no mergulho?",
    sub: "Não existe resposta errada.",
    options: [
      { key: "vida-marinha", label: "Ver peixes tropicais de perto" },
      { key: "flutuar", label: "A sensação de flutuar sem gravidade" },
      { key: "adrenalina", label: "Viver algo diferente e marcante" },
      { key: "fotos", label: "Levar fotos e memórias inesquecíveis" },
    ],
  },
  {
    id: "companhia",
    label: "Pergunta 3 de 8",
    title: "Com quem você quer viver essa aventura?",
    sub: "Todo mundo tem seu ritmo.",
    options: [
      { key: "sozinho", label: "Sozinho(a) — é o meu momento" },
      { key: "casal", label: "Com o(a) meu(minha) parceiro(a)" },
      { key: "amigos", label: "Com um(a) amigo(a)" },
      { key: "familia", label: "Com a família" },
    ],
  },
  {
    id: "sabia-porto-belo",
    label: "Pergunta 4 de 8",
    title:
      "Você sabia que a costa de Porto Belo é uma das águas mais transparentes do Sul do Brasil, com peixes tropicais o ano todo?",
    sub: "Muitos comparam com destinos como Fernando de Noronha.",
    options: [
      { key: "nao-sabia", label: "Não sabia! Que incrível" },
      { key: "sabia", label: "Sim, e por isso estou aqui" },
      { key: "quero-ver", label: "Mal posso esperar pra ver" },
    ],
  },
  {
    id: "medo-nadar",
    label: "Pergunta 5 de 8",
    title:
      "Você não precisa saber nadar bem nem ter experiência: nosso instrutor certificado vai contigo o tempo todo. Isso te deixa mais tranquilo?",
    sub: "Segurança total, do briefing ao retorno.",
    options: [
      { key: "muito", label: "Muito! Isso muda tudo" },
      { key: "ja-sabia", label: "Já sabia — e por isso quero fazer" },
      { key: "pronto", label: "Agora sim, estou pronto(a)" },
    ],
  },
  {
    id: "quantos-ingressos",
    label: "Pergunta 6 de 8",
    title: "Quantas pessoas mergulham com você?",
    sub: "Nosso ingresso vale por 1 ano — dá pra vir 2x ou trazer alguém.",
    options: [
      { key: "1", label: "Só eu" },
      { key: "2", label: "2 pessoas" },
      { key: "3+", label: "3 ou mais" },
    ],
  },
  {
    id: "quando",
    label: "Pergunta 7 de 8",
    title: "Quando você quer viver esse mergulho?",
    sub: "Você compra hoje e agenda depois — sem pressa.",
    options: [
      { key: "asap", label: "No próximo fim de semana" },
      { key: "30d", label: "Nos próximos 30 dias" },
      { key: "depois", label: "Ainda vou decidir a data" },
    ],
  },
  {
    id: "oferta",
    label: "Pergunta 8 de 8",
    title:
      "Se hoje tivesse uma promoção com 50% OFF — 2 mergulhos pelo preço de 1 — você aceitaria essa vaga?",
    sub: "Últimas vagas com desconto para esta temporada.",
    options: [
      { key: "aceito", label: "Sim, quero garantir minha vaga!" },
      { key: "quero-saber", label: "Sim, mas quero ver os detalhes primeiro" },
    ],
  },
];

export default function Quiz() {
  const [step, setStep] = useState(0); // 0..7 questions, 8 = final form
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ nome: "", email: "", telefone: "" });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    document.title = "Quiz do Mergulho — Dive Master";
    // Meta Pixel: ViewContent when the quiz starts
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "ViewContent", { content_name: "Quiz do Mergulho" });
    }
    window.scrollTo(0, 0);
  }, []);

  const total = QUESTIONS.length;
  const progressPct = useMemo(
    () => Math.min(100, Math.round(((step + (step === total ? 0 : 0)) / total) * 100)),
    [step, total]
  );

  const q = step < total ? QUESTIONS[step] : null;

  const handleSelect = (option) => {
    if (!q) return;
    // Meta Pixel: micro-conversion for each answer (helps FB optimize)
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", "QuizStep", { step: step + 1, question: q.id, answer: option.key });
    }
    setAnswers((prev) => ({ ...prev, [q.id]: option.key }));
    // slight delay for tactile feedback
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    if (!form.nome || !form.email || !form.telefone) return;
    setStatus("loading");
    try {
      const dispositivo =
        typeof window !== "undefined" && window.innerWidth < 768 ? "Mobile" : "Desktop";
      await axios.post(`${API}/leads`, {
        ...form,
        origem: "Quiz",
        dispositivo,
      });
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "Quiz do Mergulho" });
        window.fbq("track", "InitiateCheckout", { currency: "BRL", value: 580 });
      }
      // Send straight to checkout
      window.location.href = CHECKOUT_URL;
    } catch (err) {
      setStatus("idle");
      alert("Não foi possível enviar. Tente novamente em instantes.");
    }
  };

  return (
    <main
      className="relative min-h-[100svh] w-full overflow-hidden light-rays"
      style={{ background: "linear-gradient(180deg,#0F3A86,#05123A)" }}
      data-testid="quiz-page"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-ocean-deep/40 via-transparent to-ocean-deep/80" />
      <Bubbles count={22} />

      {/* Top bar */}
      <div className="relative z-10 mx-auto flex w-full max-w-2xl items-center justify-between px-5 pt-6 sm:pt-10">
        <img src={IMG.logo} alt="Dive Master" className="h-10 w-auto drop-shadow-lg sm:h-12" />
        <div className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur">
          Batismo de Mergulho
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative z-10 mx-auto mt-5 w-full max-w-2xl px-5">
        <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-sun to-brand-red"
            initial={{ width: 0 }}
            animate={{
              width: `${Math.min(100, Math.round(((step + (step === total ? 1 : 0)) / total) * 100))}%`,
            }}
            transition={{ duration: 0.5, ease: EASE }}
            data-testid="quiz-progress"
          />
        </div>
        <p className="mt-2 text-right text-[11px] font-bold uppercase tracking-[0.22em] text-white/60">
          {step < total ? `${step + 1}/${total}` : "Concluído"}
        </p>
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-2xl px-5 pb-16 pt-8 sm:pt-10">
        <AnimatePresence mode="wait">
          {q ? (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.55, ease: EASE }}
              data-testid={`quiz-question-${step + 1}`}
            >
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-ocean-glow">
                {q.label}
              </p>
              <h1 className="mt-3 font-display text-3xl font-black leading-[1.05] text-white sm:text-4xl lg:text-5xl">
                {q.title}
              </h1>
              {q.sub && (
                <p className="mt-3 text-sm text-white/70 sm:text-base">{q.sub}</p>
              )}

              <div className="mt-8 space-y-3">
                {q.options.map((opt, i) => (
                  <motion.button
                    key={opt.key}
                    onClick={() => handleSelect(opt)}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 * i, ease: EASE }}
                    whileHover={{ scale: 1.015, x: 2 }}
                    whileTap={{ scale: 0.98 }}
                    className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 text-left text-base font-semibold text-white backdrop-blur-md transition-colors hover:border-sun/70 hover:bg-white/20 sm:text-lg"
                    data-testid={`quiz-answer-${step + 1}-${opt.key}`}
                  >
                    <span>{opt.label}</span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sun/20 text-sun transition-colors group-hover:bg-sun group-hover:text-white">
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </motion.button>
                ))}
              </div>

              {/* Social proof band */}
              <div className="mt-10 flex items-center gap-4 text-[11px] font-semibold uppercase tracking-widest text-white/60">
                <div className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 fill-sun text-sun" />
                  <Star className="h-3.5 w-3.5 fill-sun text-sun" />
                  <Star className="h-3.5 w-3.5 fill-sun text-sun" />
                  <Star className="h-3.5 w-3.5 fill-sun text-sun" />
                  <Star className="h-3.5 w-3.5 fill-sun text-sun" />
                </div>
                <span>+500 mergulhadores já viveram</span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="final"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              data-testid="quiz-final"
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand-red/90 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-white shadow-lg shadow-brand-red/30">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-white" />
                Vaga liberada — 50% OFF
              </div>

              <h1 className="font-display text-3xl font-black leading-[1.02] text-white sm:text-4xl lg:text-5xl">
                Prontinho!<br />
                <span className="text-sun">Sua vaga com 50% de desconto está reservada.</span>
              </h1>
              <p className="mt-4 text-base text-white/85 sm:text-lg">
                Preencha seus dados. Após a compra do(s) ingresso(s), nossa equipe entrará em contato enviando a <strong className="text-white">agenda virtual</strong> para você escolher a melhor data para agendar seu mergulho.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-3" data-testid="quiz-form">
                <QField
                  id="nome"
                  placeholder="Seu nome completo"
                  value={form.nome}
                  onChange={(v) => setForm((f) => ({ ...f, nome: v }))}
                  testid="quiz-input-nome"
                />
                <QField
                  id="email"
                  type="email"
                  placeholder="Seu melhor e-mail"
                  value={form.email}
                  onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                  testid="quiz-input-email"
                />
                <QField
                  id="telefone"
                  type="tel"
                  placeholder="Seu WhatsApp (com DDD)"
                  value={form.telefone}
                  onChange={(v) => setForm((f) => ({ ...f, telefone: v }))}
                  testid="quiz-input-telefone"
                />

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={status === "loading"}
                  className="cta-sun mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-5 text-base font-extrabold uppercase tracking-wide text-white shadow-2xl shadow-sun/40 disabled:opacity-70 sm:text-lg"
                  data-testid="quiz-submit"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> Enviando...
                    </>
                  ) : (
                    <>
                      Liberar meu ingresso com 50% OFF
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </motion.button>
              </form>

              {/* Trust bar */}
              <div className="mt-6 grid grid-cols-3 gap-3 text-center text-[11px] font-semibold uppercase tracking-wider text-white/70">
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <ShieldCheck className="mx-auto mb-1 h-4 w-4 text-ocean-glow" />
                  Compra segura
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <Users className="mx-auto mb-1 h-4 w-4 text-ocean-glow" />
                  Instrutor certificado
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <Star className="mx-auto mb-1 h-4 w-4 text-ocean-glow" />
                  Voucher 1 ano
                </div>
              </div>

              <p className="mt-4 text-center text-[11px] text-white/50">
                Dados protegidos. Você compra hoje e agenda depois.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom wave */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 leading-[0]">
        <svg viewBox="0 0 390 70" preserveAspectRatio="none" className="block h-[70px] w-full sm:h-[110px]">
          <path
            d="M0,44 C100,80 160,24 230,32 C300,40 340,64 390,42 L390,70 L0,70 Z"
            fill="rgba(255,255,255,0.05)"
          />
        </svg>
      </div>
    </main>
  );
}

function QField({ id, type = "text", placeholder, value, onChange, testid }) {
  return (
    <input
      id={id}
      name={id}
      type={type}
      required
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-2xl border border-white/15 bg-white/10 px-5 py-4 text-base text-white placeholder-white/50 outline-none backdrop-blur-md transition-colors focus:border-sun focus:bg-white/15"
      data-testid={testid}
    />
  );
}
